import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import teamAPI from "../../../services/teamApi";
import clinicAPI from "../../../services/clinicApi";
import { hasAccessToken } from "../../../utils/authStorage";
import { getClinicPortalLinks } from "../../../utils/clinicPortalLinks";
import { HubFormCard } from "../AdminPortal/HubPwa";
import TrainerAssignmentsFields, { toggleId } from "./TrainerAssignmentsFields";
import s from "../clinic.module.css";
import aa from "../AdminPortal/adminApp.module.css";
import {
  Button,
  Field,
  Input,
  Select,
  Textarea,
  useToast,
} from "../../ui";

const EMPTY_FORM = {
  role: "trainer",
  fullName: "",
  email: "",
  password: "",
  sendInvite: true,
  description: "",
  branchIds: [],
  directionIds: [],
  groupIds: [],
};

export default function TeamInviteForm({
  onSuccess,
  onCancel,
  sheet = false,
  formId,
  onLoadingChange,
}) {
  const toast = useToast();
  const { tenant } = useSelector((state) => state.auth);
  const [branches, setBranches] = useState([]);
  const [directions, setDirections] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const staffPortalUrl =
    getClinicPortalLinks(tenant?.subdomain).find((l) => l.id === "staff")?.url ||
    "";

  useEffect(() => {
    setForm(EMPTY_FORM);
    clinicAPI.getSportDirections().then((r) => setDirections(r.directions || [])).catch(() => {});
    clinicAPI
      .getSpecializations()
      .then((r) =>
        setGroups((r.specializations || []).filter((x) => x.is_active !== false)),
      )
      .catch(() => setGroups([]));
    clinicAPI.getBranches().then((r) => {
      const list = (r.branches || []).filter((b) => b.is_active !== false);
      setBranches(list);
      if (list.length === 1) {
        setForm((f) => ({ ...f, branchIds: [list[0].id] }));
      }
    });
  }, []);

  const toggleBranch = (id) => {
    setForm((f) => ({
      ...f,
      branchIds: toggleId(f.branchIds, id),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!hasAccessToken()) {
      toast.error("Сессия истекла. Войдите снова.");
      return;
    }
    if (!form.sendInvite && !form.password) {
      toast.error("Укажите пароль или включите отправку приглашения на email");
      return;
    }

    setLoading(true);
    try {
      const res = await teamAPI.invite({
        role: form.role,
        email: form.email.trim(),
        fullName: form.fullName.trim(),
        password: form.sendInvite ? undefined : form.password,
        send_invite: form.sendInvite,
        description:
          form.role === "trainer" ? form.description?.trim() || undefined : undefined,
        branchIds:
          form.role === "trainer" && form.branchIds.length
            ? form.branchIds
            : undefined,
        directionIds: form.role === "trainer" ? form.directionIds : undefined,
        groupIds: form.role === "trainer" ? form.groupIds : undefined,
      });

      if (res?.result !== 0) {
        toast.error(res?.error || "Не удалось пригласить");
        return;
      }

      const inviteUrl = res.invite_url || res.devInviteUrl;
      if (inviteUrl && !res.email_sent) {
        try {
          await navigator.clipboard.writeText(inviteUrl);
          toast.success(
            res.invite_note || "Ссылка приглашения скопирована",
            8000,
          );
        } catch {
          toast.success(res.message || "Сотрудник добавлен", 8000);
        }
      } else {
        toast.success(res.message || "Сотрудник добавлен", 8000);
      }
      onSuccess?.(res);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка");
    } finally {
      setLoading(false);
    }
  };

  const inviteBlock = !form.sendInvite ? (
    <Field label="Пароль" hint="Минимум 8 символов">
      <Input
        type="password"
        required={!form.sendInvite}
        minLength={8}
        value={form.password}
        onChange={(e) => setForm({ ...form, password: e.target.value })}
      />
    </Field>
  ) : (
    <p className={sheet ? aa.teamInviteHintBox : aa.hubHint}>
      Код клуба: <strong>{tenant?.subdomain || "—"}</strong>
      {staffPortalUrl ? (
        <>
          {" "}
          · портал:{" "}
          <a href={staffPortalUrl} target="_blank" rel="noreferrer">
            {staffPortalUrl.replace(/^https?:\/\//, "")}
          </a>
        </>
      ) : null}
    </p>
  );

  const branchPickList =
    branches.length === 0 ? (
      <p className={aa.hubHint}>Сначала добавьте зал в разделе «Залы»</p>
    ) : (
      <div className={aa.teamInvitePickList}>
        {branches.map((b) => {
          const checked = form.branchIds.includes(b.id);
          return (
            <label
              key={b.id}
              className={`${aa.teamInvitePickItem} ${
                checked ? aa.teamInvitePickItemOn : ""
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleBranch(b.id)}
              />
              <span className={aa.teamInvitePickLabel}>{b.name}</span>
            </label>
          );
        })}
      </div>
    );

  if (sheet) {
    return (
      <form id={formId} onSubmit={submit} className={aa.teamInviteForm}>
        <HubFormCard title="Сотрудник">
          <Field label="Роль">
            <Select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="trainer">Тренер</option>
              <option value="admin">Администратор</option>
            </Select>
          </Field>

          <Field label="ФИО">
            <Input
              required
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              placeholder="Иванов Иван"
              autoFocus
            />
          </Field>

          <Field label="Email">
            <Input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="email@example.com"
            />
          </Field>

          <label className={aa.teamInviteToggleCard}>
            <input
              type="checkbox"
              checked={form.sendInvite}
              onChange={(e) =>
                setForm({ ...form, sendInvite: e.target.checked })
              }
            />
            <span className={aa.teamInviteToggleText}>
              <strong>Отправить приглашение на email</strong>
              <span>Ссылка для входа придёт автоматически</span>
            </span>
          </label>

          {inviteBlock}
        </HubFormCard>

        {form.role === "trainer" ? (
          <>
            <HubFormCard title="Назначения">
              <TrainerAssignmentsFields
                sheet
                directions={directions}
                groups={groups}
                directionIds={form.directionIds}
                groupIds={form.groupIds}
                onToggleDirection={(id) =>
                  setForm((f) => ({
                    ...f,
                    directionIds: toggleId(f.directionIds, id),
                  }))
                }
                onToggleGroup={(id) =>
                  setForm((f) => ({
                    ...f,
                    groupIds: toggleId(f.groupIds, id),
                  }))
                }
              />

              <div className={aa.teamInvitePickSection}>
                <p className={aa.teamInvitePickTitle}>Залы (необязательно)</p>
                {branchPickList}
              </div>
            </HubFormCard>

            <HubFormCard title="Дополнительно">
              <Field label="Описание">
                <Textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Необязательно"
                />
              </Field>
            </HubFormCard>
          </>
        ) : (
          <p className={aa.teamInviteAdminHint}>
            Администратор видит весь клуб: клиенты, расписание, настройки и
            команду.
          </p>
        )}
      </form>
    );
  }

  const checkClass = s.branchCheckGrid;
  const checkItemClass = s.branchCheckItem;

  return (
    <form id={formId} onSubmit={submit}>
      <Field label="Роль">
        <Select
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
        >
          <option value="trainer">Тренер</option>
          <option value="admin">Администратор</option>
        </Select>
      </Field>

      <Field label="ФИО">
        <Input
          required
          value={form.fullName}
          onChange={(e) => setForm({ ...form, fullName: e.target.value })}
          placeholder="Иванов Иван"
        />
      </Field>

      <Field label="Email">
        <Input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="email@example.com"
        />
      </Field>

      <label className={aa.teamInviteToggle}>
        <input
          type="checkbox"
          checked={form.sendInvite}
          onChange={(e) =>
            setForm({ ...form, sendInvite: e.target.checked })
          }
        />
        <span>Отправить приглашение на email</span>
      </label>

      {inviteBlock}

      {form.role === "trainer" ? (
        <>
          <p className={aa.hubSectionLabel}>Направления и группы</p>
          <TrainerAssignmentsFields
            directions={directions}
            groups={groups}
            directionIds={form.directionIds}
            groupIds={form.groupIds}
            onToggleDirection={(id) =>
              setForm((f) => ({
                ...f,
                directionIds: toggleId(f.directionIds, id),
              }))
            }
            onToggleGroup={(id) =>
              setForm((f) => ({
                ...f,
                groupIds: toggleId(f.groupIds, id),
              }))
            }
          />

          <Field label="Описание">
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="Необязательно"
            />
          </Field>

          <p className={aa.hubSectionLabel}>Залы (необязательно)</p>
          <div className={checkClass}>
            {branches.length === 0 ? (
              <p className={aa.hubHint}>Сначала добавьте зал в разделе «Залы»</p>
            ) : (
              branches.map((b) => (
                <label key={b.id} className={checkItemClass}>
                  <input
                    type="checkbox"
                    checked={form.branchIds.includes(b.id)}
                    onChange={() => toggleBranch(b.id)}
                  />
                  <span>{b.name}</span>
                </label>
              ))
            )}
          </div>
        </>
      ) : (
        <p className={aa.hubHint}>
          Администратор видит весь клуб: клиенты, расписание, настройки и команду.
        </p>
      )}

      <div
        style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}
      >
        <Button type="submit" disabled={loading}>
          {loading ? "Отправка…" : "Пригласить"}
        </Button>
        {onCancel ? (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Отмена
          </Button>
        ) : null}
      </div>
    </form>
  );
}
